import axiosInstance from '@/utils/interceptor'

export interface AxiomSystemInfoComponent {
  version: string
  machine_name?: string
}

// Keyed by component name (shape not fixed yet - render generically).
export type ComponentStatusMap = Record<string, unknown>

export interface StatusResponse {
  info_msg: string
  warn_msg: string
  critical_msg: string
  status_msg: string
  status_code: number
  host: string
  start_time: string
  timestamp: string
  uptime: string
  version: string
  systemInfo: Record<string, AxiomSystemInfoComponent>
  components: {
    ok: ComponentStatusMap
    warning: ComponentStatusMap
    critical: ComponentStatusMap
  }
}

export interface SchemaVersionResponse {
  version: string
}

const CACHE_TTL_MS = 60 * 1000

class StatusService {
  // Caches the in-flight/resolved request for CACHE_TTL_MS so rapid dialog
  // toggling (e.g. someone tapping Ctrl+Alt+S repeatedly) doesn't hammer the
  // endpoint. Cleared on failure so a later call can retry sooner than the TTL.
  private cachedRequest: Promise<StatusResponse> | null = null
  private cachedAt = 0

  // Same caching approach as getStatus(), kept in its own pair of fields so
  // the two endpoints' caches don't invalidate each other.
  private cachedSchemaVersionRequest: Promise<SchemaVersionResponse> | null =
    null
  private cachedSchemaVersionAt = 0

  getStatus(): Promise<StatusResponse> {
    const isStale = Date.now() - this.cachedAt >= CACHE_TTL_MS
    if (!this.cachedRequest || isStale) {
      this.cachedAt = Date.now()
      this.cachedRequest = axiosInstance
        .get('/sysInfo/full')
        .then((response) => response.data)
        .catch((error) => {
          console.error('Error fetching status:', error)
          this.cachedRequest = null
          throw error
        })
    }
    return this.cachedRequest
  }

  getSchemaVersion(): Promise<SchemaVersionResponse> {
    const isStale = Date.now() - this.cachedSchemaVersionAt >= CACHE_TTL_MS
    if (!this.cachedSchemaVersionRequest || isStale) {
      this.cachedSchemaVersionAt = Date.now()
      this.cachedSchemaVersionRequest = axiosInstance
        .get('/reportDashboard/api/v1/schema-version')
        .then((response) => response.data)
        .catch((error) => {
          console.error('Error fetching schema version:', error)
          this.cachedSchemaVersionRequest = null
          throw error
        })
    }
    return this.cachedSchemaVersionRequest
  }
}

export const statusService = new StatusService()
